<template>
  <SignInSignUpLayout>
    <b-form @submit="singUp" class="sing-in-form">
      <h1 class="sign-in-title">Sign Up for a Trial</h1>

      <CompanyUrlPrefixField
        ref="prefixComponent"
        :companyPrefixGroupId="companyPrefixGroupId"
        :hideValidationErrors="!submitButtonPressed"
        :serverErrors="prefixServerErrors"
      />

      <b-form-group :state="emailState">
        <label for="email-input" class="form__label input-label">Email</label>

        <b-form-input
          id="email-input"
          v-model.trim="$v.email.$model"
          :state="emailState"
          placeholder="Enter email"
          class="sing-up-input"
        />

        <template v-slot:invalid-feedback>
          <div v-for="(error, index) in emailErrorMessage" :key="index">
            {{ error }}
          </div>
        </template>
      </b-form-group>

      <div class="recaptcha-container" v-if="!isTestingE2E">
        <vue-recaptcha
          :load-recaptcha-script="true"
          ref="recaptcha"
          :sitekey="recaptchaKey"
          @verify="onVerify"
          @expired="onCaptchaExpired"
        ></vue-recaptcha>
        <b-form-invalid-feedback :state="isReCaptchaValid">
          Please complete the reCAPTCHA
        </b-form-invalid-feedback>
      </div>

      <b-button class="button ml-auto" type="submit" variant="primary" :disabled="isSubmitButtonDisabled">
        Sign Up
      </b-button>

      <div class="sign-with">
        <span class="text">Or sign in with</span>
      </div>
      <div class="text-center">
        <a href="javascript:void(0)" @click="signUpWithAtlassian">
          <img src="/images/atlassian.webp" :style="{height: '26px', width: 'auto', objectFit: 'contain'}" alt="jira logo"/>
        </a>
      </div>

      <TermsOfUseCheckbox ref="termsOfUseCheckboxComponent" />

      <b-form-checkbox
        v-model="acceptMarketing"
        class="sign-up-checkbox d-flex mt-2"
        :value="0"
        :unchecked-value="1"
      >
        Please don't send me marketing communications
      </b-form-checkbox>
    </b-form>
  </SignInSignUpLayout>
</template>

<script>
import VueRecaptcha from 'vue-recaptcha'
import { validationMixin } from 'vuelidate'
import { required, email } from 'vuelidate/lib/validators'
import SignUp from '@mixins/SignUp'
import SignInSignUpLayout from '@components/sign-up/SignInSignUpLayout.vue'
import CompanyUrlPrefixField from '@components/sign-up/CompanyUrlPrefixField.vue'
import TermsOfUseCheckbox from '@components/sign-up/TermsOfUseCheckbox.vue'

export default {
  name: 'signup',
  components: { SignInSignUpLayout, CompanyUrlPrefixField, VueRecaptcha, TermsOfUseCheckbox },
  mixins: [ validationMixin, SignUp ],
  data () {
    return {
      email: '',
      acceptMarketing: 1,
      recaptchaKey: process.env.VUE_APP_RECAPTCHA_SITEKEY,
      recaptchaToken: null,

      // validation related state
      submitButtonPressed: false,
      prefixServerErrors: null,
      emailServerErrors: null,

      companyPrefixGroupId: 'company-prefix-group',
      ui: {
        signUpRequestPending: false
      }
    }
  },
  validations () {
    return {
      email: {
        required,
        email
      },
      recaptchaToken: this.isTestingE2E ? {} : {
        required
      }
    }
  },
  computed: {
    emailState () {
      return this.submitButtonPressed && this.emailErrorMessage ? false : null
    },
    emailErrorMessage () {
      if (this.emailServerErrors) {
        return this.emailServerErrors
      }

      if (!this.$v.email.$error) {
        return null
      }

      if (!this.$v.email.required) {
        return [this.VALIDATION_MESSAGES.common.required]
      }

      if (!this.$v.email.email) {
        return [this.VALIDATION_MESSAGES.email.invalid]
      }

      return null
    },
    isReCaptchaValid () {
      return !this.$v.recaptchaToken.$error
    },
    isFormValid () {
      return !this.$v.$invalid && !this.$refs.prefixComponent.$v.$invalid && !this.$refs.termsOfUseCheckboxComponent.$v.$invalid
    },
    isSubmitButtonDisabled () {
      return this.ui.signUpRequestPending || (this.submitButtonPressed && !this.isFormValid)
    }
  },
  mounted () {
    const tenantSignUp = this.getTenantSignUpData()
    if (tenantSignUp) {
      this.email = tenantSignUp.email
      this.$refs.prefixComponent.prefix = tenantSignUp.prefix
    }
  },
  methods: {
    onVerify (token) {
      this.$v.recaptchaToken.$model = token
    },
    onCaptchaExpired () {
      this.$refs.recaptcha.reset()
      this.$v.recaptchaToken.$model = null
    },
    formTouch () {
      this.$v.$touch()
      this.$refs.prefixComponent.$v.$touch()
      this.$refs.termsOfUseCheckboxComponent.$v.$touch()
    },
    singUp (e) {
      e.preventDefault()

      this.submitButtonPressed = true
      this.prefixServerErrors = null
      this.emailServerErrors = null

      this.formTouch()
      if (!this.isFormValid) {
        return
      }

      this.ui.signUpRequestPending = true

      this.axios
        .post('/api/register', {
          email: this.email,
          prefix: this.$refs.prefixComponent.prefix,
          'g-recaptcha-token': this.recaptchaToken,
          acceptMarketing: this.acceptMarketing,
          isTestingE2E: this.isTestingE2E
        })
        .then((response) => {
          this.setTenantSignUpData('email', this.email)
          this.setTenantSignUpData('prefix', this.$refs.prefixComponent.prefix)
          this.setTenantSignUpData('expired', response.data.expired)

          this.$router.push(this.ROUTES.CONFIRM_EMAIL.name)
        })
        .catch((err) => {
          if (err.response?.data?.errors) {
            const { prefix, email, ...restErrors } = err.response.data.errors

            if (prefix) this.prefixServerErrors = prefix
            if (email) this.emailServerErrors = email

            const errors = Object.values(restErrors)

            for (let i = 0; i < errors.length; i++) {
              this.$snotify.error(errors[i][0], 'Error', this.$store.state.snotify)
            }
          } else {
            this.$snotify.error(err.response?.data?.error ?? err, 'Error', this.$store.state.snotify)
          }
          this.$refs.recaptcha.reset()
        })
        .finally(() => {
          this.ui.signUpRequestPending = false
        })
    },
    signUpWithAtlassian () {
      this.$refs.termsOfUseCheckboxComponent.$v.termsOfUse.$touch()
      if (!this.$refs.termsOfUseCheckboxComponent.$v.termsOfUse.$error) {
        this.axios
          .get('/api/register/generate-jira-url')
          .then((response) => {
            const jiraAuthWindow = window.open(
              response.data.url,
              'Sign up with Atlassian',
              'popup'
            )

            if (!jiraAuthWindow) {
              this.$snotify.error('Popup blocked. Please allow popups for this site.', 'Error', this.$store.state.snotify)
              return
            }

            const messageListener = async (event) => {
              const eventData = event.data
              const popupOrigin = event.origin
              const windowOrigin = window.location.origin

              // Only process messages from our domain with valid event_id
              if (windowOrigin === popupOrigin && eventData && eventData.event_id) {
                if (eventData.event_id === 'jira-signup-success') {
                  this.$router.push(this.ROUTES.ADD_AGENCY_ATLASSIAN.name)
                } else if (eventData.event_id === 'email-is-registered') {
                  const errorHtml = `
                      <div class="snotifyToast__title">Error</div>
                      <div class="snotifyToast__body">The email associated with your Atlassian account${eventData.email ? ` (${eventData.email})` : ''} is already registered in our system.<br>Please sign up manually using a different email address.</div>
                      <div class="snotify-icon snotify-icon--error"></div>`
                  this.$snotify.error('Error', {
                    ...this.$store.state.snotify,
                    html: errorHtml
                  })
                } else if (eventData.event_id === 'auth-failed') {
                  this.$snotify.error('Atlassian authorization failed', 'Error', this.$store.state.snotify)
                }

                if (['jira-signup-success', 'email-is-registered', 'auth-failed'].includes(eventData.event_id)) {
                  window.removeEventListener('message', messageListener)
                  if (!jiraAuthWindow.closed) {
                    jiraAuthWindow.close()
                  }
                }
                // Don't close for other event_id values - they might be spurious
              }
            }

            window.addEventListener('message', messageListener)

            // Clean up if popup is closed manually
            const checkClosed = setInterval(() => {
              if (jiraAuthWindow.closed) {
                window.removeEventListener('message', messageListener)
                clearInterval(checkClosed)
              }
            }, 1000)
          })
          .catch((err) => {
            if (err.response?.data?.errors) {
              this.errorNotifications(err)
            }
          })
      }
    }
  }
}
</script>

<style scoped lang="scss">
.sign-up-datepicker {
  position: relative;
  margin-bottom: 20px;
  .form-control[readonly] {
    background-color: transparent;
  }
}

.recaptcha-container {
  display: flex;
  flex-direction: column;
  align-items: center;

  ::v-deep .invalid-feedback {
    text-align: center;
    margin-left: 0;
  }
}
</style>
